import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  PageHeader,
  LoadingState,
  StatusChip,
  DetailCard,
  InfoRow,
  ConfirmDialog,
} from '../components';
import {
  useProduct,
  useUpdateStock,
  useDeleteProduct,
} from '../hooks/useProducts';
import { colors, formatCurrency, formatDate } from '../constants';

export default function ProductDetail() {
  const { uuid } = useParams();
  const history = useHistory();
  const { data: product, isLoading } = useProduct(uuid);
  const updateStock = useUpdateStock();
  const deleteProduct = useDeleteProduct();
  const [stockQty, setStockQty] = useState('');
  const [operation, setOperation] = useState('increase');
  const [showDelete, setShowDelete] = useState(false);
  const [toast, setToast] = useState(null);

  if (isLoading) return <LoadingState />;
  if (!product) {
    return (
      <>
        <PageHeader title="Not Found" backTo="/inventory-management/products" />
        <Typography sx={{ color: colors.gray500 }}>Item not found</Typography>
      </>
    );
  }

  const handleStockUpdate = async () => {
    const qty = parseFloat(stockQty);
    if (Number.isNaN(qty) || qty < 0) return;
    try {
      await updateStock.mutateAsync({
        sku: product.sku,
        data: { operation, quantity: qty },
      });
      setStockQty('');
      setToast({ severity: 'success', message: 'Stock updated' });
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Update failed' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProduct.mutateAsync(product.uuid);
      setShowDelete(false);
      history.push('/inventory-management/products');
    } catch (e) {
      setShowDelete(false);
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Delete failed' });
    }
  };

  return (
    <>
      <PageHeader
        title={product.name}
        subtitle={`SKU: ${product.sku}`}
        backTo="/inventory-management/products"
        actions={<StatusChip status={product.isActive ? 'active' : 'inactive'} />}
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <DetailCard title="Details">
            <InfoRow label="Description" value={product.description} />
            <InfoRow label="Unit" value={product.unit} />
            <InfoRow label="Unit Cost" value={formatCurrency(product.unitPrice)} />
            <InfoRow label="Current Stock" value={product.stock} />
            <InfoRow label="Min Stock" value={product.minStockLevel} />
            <InfoRow label="Max Stock" value={product.maxStockLevel} />
            <InfoRow label="Track Inventory" value={product.trackInventory ? 'Yes' : 'No'} />
            <InfoRow label="Barcodes" value={product.barcodeNumber?.join(', ')} />
            {product.expiryDate && (
              <InfoRow label="Expiry Date" value={formatDate(product.expiryDate)} />
            )}
          </DetailCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <DetailCard title="Quick Stock Adjustment">
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={operation}
              onChange={(_, v) => v && setOperation(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="increase">Increase</ToggleButton>
              <ToggleButton value="decrease">Decrease</ToggleButton>
              <ToggleButton value="set">Set</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Quantity"
                value={stockQty}
                onChange={(e) => setStockQty(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleStockUpdate}
                disabled={updateStock.isPending || !stockQty}
                sx={{ backgroundColor: colors.primary, textTransform: 'none', minWidth: 90 }}
              >
                Apply
              </Button>
            </Stack>
          </DetailCard>

          <Box sx={{ mt: 2.5 }}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setShowDelete(true)}
              sx={{ textTransform: 'none' }}
            >
              Remove Product
            </Button>
          </Box>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={showDelete}
        title="Remove Product"
        message={`Are you sure you want to remove "${product.name}"?`}
        confirmLabel="Remove"
        destructive
        loading={deleteProduct.isPending}
        onConfirm={handleDelete}
        onClose={() => setShowDelete(false)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
